/**
 * 名前だけを並べる分類マスターの一覧（動詞・単位）の本体。
 *
 * 一覧の問いは「どれが使えるか（目当てを開く）」だけなので、表は名前の 1 列にする（Dhik
 * 2026-09-29「本当に出すべき情報を 1 or 2 個に」）。型ごとの欄は、補足として名前の title に
 * 回すもの（単位の表し方）と、詳細で読めば足りるもの（動詞の名詞形・層）に分け、表には並べない。
 * 未翻訳の印も名前の title に回す。行を押すとマスター詳細（`/masters/$type/$id`）を開く。案件を
 * 選んでいれば案件を持ったまま開く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 動詞・単位の一覧を名前の 1 列の表で描き、補足は名前の title に回し、行からマスター詳細へ繋ぐ。
 * @graph-connects MASTER_DETAIL_ROUTE [references] マスター詳細
 */
import type { MasterDetailType, MasterListItem } from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '../components/spec/fallback-states.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { pjLinkSearch } from './pj-link.js';
import { MASTER_DETAIL_ROUTE } from './route-paths.js';

/** ローディング時に出すスケルトンの行数。 @graph-connects none */
const SKELETON_ROWS = 8;

/** 表のセルの共通 class。 @graph-connects none */
const CELL_CLASS = 'border-b border-[var(--color-border)] px-3 py-2 text-left align-top text-sm';

/** `MasterTableListPage` の props。 */
export interface MasterTableListPageProps {
  /** 行を押したときに開く詳細の型。 */
  type: MasterDetailType;
  /** 見出しの翻訳キー。 */
  titleKey: string;
  /** 名前の列の見出しの翻訳キー。 */
  nameKey: string;
  /** 0 件のときの翻訳キー。 */
  emptyKey: string;
  /** 名前の title に添える補足の 1 行。省けば補足を出さない。 */
  hint?: (item: MasterListItem) => string;
  /** 一覧の中身。取得前なら undefined。 */
  items: readonly MasterListItem[] | undefined;
  /** 取得中か。 */
  isLoading: boolean;
  /** 取得エラー。 */
  error: unknown;
  /** いま重ねている案件番号。詳細へ引き継ぐ。 */
  pj?: string;
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 名前だけを持つ分類マスターの一覧を、見出しと名前の 1 列の表で描く。
 * @graph-connects MASTER_DETAIL_ROUTE [references] マスター詳細
 * @graph-connects displayLabel [calls] 表示名として実際に描く文字列を決める
 */
export function MasterTableListPage({
  type,
  titleKey,
  nameKey,
  emptyKey,
  hint,
  items,
  isLoading,
  error,
  pj,
}: MasterTableListPageProps) {
  const { t } = useTranslation();

  if (error !== null && error !== undefined) {
    return <ErrorState error={error} />;
  }

  const rows = items ?? [];
  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className="flex flex-col gap-1" aria-busy="true" aria-label={t('common.loading')}>
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <Skeleton key={index} variant="rectangular" height="2rem" />
        ))}
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <p role="status" className="text-sm text-[var(--color-text-muted)]">
        {t(emptyKey)}
      </p>
    );
  } else {
    body = (
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th
              scope="col"
              className={`${CELL_CLASS} text-xs font-bold text-[var(--color-text-muted)]`}
            >
              {t(nameKey)}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => {
            const notes = [
              hint?.(item),
              item.label !== '' && item.untranslated ? t('spec.untranslated') : undefined,
            ].filter((note) => note !== undefined);
            return (
              <tr key={item.id} data-id={item.id}>
                <td className={CELL_CLASS}>
                  <Link
                    to={MASTER_DETAIL_ROUTE}
                    params={{ type, id: String(item.id) }}
                    {...pjLinkSearch(pj)}
                    title={notes.length === 0 ? undefined : notes.join('\n')}
                    className="text-[var(--color-accent-primary)] underline"
                  >
                    {displayLabel(item.label, t, item.untranslated)}
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      <header>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t(titleKey)}</h1>
      </header>
      {body}
    </div>
  );
}
